import type { Event } from "@mux/sdk/v2/client"
import type { QueuedServerEvent } from "./global-sdk"
import { createServerEventQueue } from "./server-event-queue"

export const eventDispatchMode = (visibilityState: string | undefined) =>
  visibilityState === "hidden" ? "task" : "frame"

export const eventRequiresImmediateDispatch = (type: string | undefined) =>
  type === "session.idle" || type === "session.error" || type === "permission.asked" || type === "question.asked"

export const eventIsInitialTextDelta = (event: QueuedServerEvent) => {
  if (event.payload.type !== "message.part.delta") return false
  const properties = event.payload.properties as { field?: string; byteOffset?: number }
  return properties.field === "text" && properties.byteOffset === 0
}

function eventReplacementKey(directory: string, payload: Event) {
  if (payload.type === "session.status") return `session.status:${directory}:${payload.properties.sessionID}`
  if (payload.type === "lsp.updated") return `lsp.updated:${directory}`
}

export function coalesceServerEvents(events: QueuedServerEvent[]) {
  const queue = createServerEventQueue()
  for (const event of events) queue.push(event)
  return queue.take()
}

const DEFAULT_DISPATCH_BUDGET_MS = 6

/** How often a background chat's held events apply. */
export const BACKGROUND_DISPATCH_MS = 250

const property = (value: unknown, key: string): unknown =>
  typeof value === "object" && value !== null && key in value ? (value as Record<string, unknown>)[key] : undefined

/**
 * The chat whose run or transcript an event reports: `sessionID` on run and
 * transcript events, on a part, or on a message. A chat's own record
 * (`session.updated`) carries `info.id` instead and has none.
 */
export function eventSessionID(payload: Event): string | undefined {
  const properties = property(payload, "properties")
  const id =
    property(properties, "sessionID") ??
    property(property(properties, "part"), "sessionID") ??
    property(property(properties, "info"), "sessionID")
  return typeof id === "string" && id ? id : undefined
}

/**
 * Mounted chats the user is not looking at, such as Grid windows other than
 * the focused one. Their events are held and applied together every
 * {@link BACKGROUND_DISPATCH_MS}, so streaming in them costs a few renders a
 * second instead of one every frame.
 */
export type BackgroundChats = {
  /** Changes whenever the set of background chats does. */
  revision: () => number
  has: (sessionID: string) => boolean
}

// This budget yields between events; it cannot preempt one expensive reactive update.
// Keep input latency and long tasks measured before increasing it or adding another stream delay.
type SchedulerOptions = {
  emit: (event: QueuedServerEvent) => void
  visibility: () => string | undefined
  background?: BackgroundChats
  now?: () => number
  scheduleTask?: (callback: () => void) => ReturnType<typeof setTimeout>
  scheduleDelayed?: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>
  requestFrame?: (callback: () => void) => number
  cancelFrame?: (id: number) => void
  dispatchBudgetMs?: number
}

/** Applies event coalescing and browser backpressure without owning the SSE connection. */
export function createServerEventScheduler(options: SchedulerOptions) {
  const now = options.now ?? (() => performance.now())
  const scheduleTask = options.scheduleTask ?? ((callback) => setTimeout(callback, 0))
  const scheduleDelayed = options.scheduleDelayed ?? ((callback, ms) => setTimeout(callback, ms))
  const requestFrame = options.requestFrame ?? ((callback) => requestAnimationFrame(callback))
  const cancelFrame = options.cancelFrame ?? ((id) => cancelAnimationFrame(id))
  const dispatchBudgetMs = options.dispatchBudgetMs ?? DEFAULT_DISPATCH_BUDGET_MS
  const canScheduleFrame = options.requestFrame !== undefined || typeof requestAnimationFrame === "function"
  const queue = createServerEventQueue()
  // Background chats' events, applied every BACKGROUND_DISPATCH_MS.
  const held = createServerEventQueue()
  let heldTimer: ReturnType<typeof setTimeout> | undefined
  let backgroundRevision = options.background?.revision()
  const dispatch: QueuedServerEvent[] = []
  let dispatchIndex = 0
  let scheduled = false
  let frame: number | undefined
  let flushTimer: ReturnType<typeof setTimeout> | undefined
  let dispatchTimer: ReturnType<typeof setTimeout> | undefined
  const waiters = new Set<() => void>()
  const resolveWaiters = () => {
    for (const resolve of waiters) resolve()
    waiters.clear()
  }

  const hasDispatch = () => dispatchIndex < dispatch.length
  const clearDispatch = () => {
    dispatch.length = 0
    dispatchIndex = 0
  }
  const clearHeldTimer = () => {
    if (heldTimer !== undefined) clearTimeout(heldTimer)
    heldTimer = undefined
  }
  // A chat's events are all in `queue` or all in `held`: they change sides only
  // here, with both emptied first, so each chat's events apply in order.
  const takeAll = () => {
    clearHeldTimer()
    if (held.length) dispatch.push(...held.take())
    if (queue.length) dispatch.push(...queue.take())
  }
  const reclassify = () => {
    const revision = options.background?.revision()
    if (revision === backgroundRevision) return
    backgroundRevision = revision
    takeAll()
  }
  const isHeld = (event: QueuedServerEvent) => {
    const background = options.background
    if (!background) return false
    const sessionID = eventSessionID(event.payload)
    return !!sessionID && background.has(sessionID)
  }
  const drain = () => {
    if (dispatchTimer !== undefined) clearTimeout(dispatchTimer)
    dispatchTimer = undefined
    const started = now()
    while (hasDispatch()) {
      options.emit(dispatch[dispatchIndex++]!)
      if (now() - started >= dispatchBudgetMs) break
    }
    if (hasDispatch()) dispatchTimer = scheduleTask(drain)
    else {
      clearDispatch()
      if (!queue.length && !held.length) resolveWaiters()
    }
  }
  const flush = () => {
    scheduled = false
    if (frame !== undefined) cancelFrame(frame)
    frame = undefined
    if (flushTimer !== undefined) clearTimeout(flushTimer)
    flushTimer = undefined
    reclassify()
    if (queue.length) dispatch.push(...queue.take())
    if (hasDispatch()) drain()
  }
  const releaseHeld = () => {
    clearHeldTimer()
    reclassify()
    if (held.length) dispatch.push(...held.take())
    if (hasDispatch()) drain()
  }
  const schedule = () => {
    if (scheduled) return
    scheduled = true
    if (eventDispatchMode(options.visibility()) === "frame" && canScheduleFrame) {
      frame = requestFrame(flush)
    } else {
      flushTimer = scheduleTask(flush)
    }
  }
  const enqueue = (event: QueuedServerEvent) => {
    reclassify()
    // A chat's run ending or asking for the user applies at once, after
    // everything held before it, its own chat's held events included.
    if (eventRequiresImmediateDispatch(event.payload.type)) {
      flush()
      takeAll()
      dispatch.push(event)
      drain()
      return
    }
    if (isHeld(event)) {
      held.push(event, eventReplacementKey(event.directory, event.payload))
      if (held.pressured) releaseHeld()
      else heldTimer ??= scheduleDelayed(releaseHeld, BACKGROUND_DISPATCH_MS)
      return
    }
    // The initial text must not wait behind the first animation-frame boundary.
    // Flush its preceding part/message lifecycle events first so reducer ordering
    // remains intact; sustained deltas still use frame coalescing.
    if (eventIsInitialTextDelta(event) && options.visibility() !== "hidden") {
      flush()
      dispatch.push(event)
      drain()
      return
    }
    queue.push(event, eventReplacementKey(event.directory, event.payload))
    schedule()
  }
  const visibilityChanged = () => {
    if (options.visibility() !== "hidden") return
    if (frame !== undefined) cancelFrame(frame)
    frame = undefined
    if (scheduled) {
      if (flushTimer !== undefined) clearTimeout(flushTimer)
      flushTimer = scheduleTask(flush)
    }
    if (hasDispatch() && dispatchTimer === undefined) dispatchTimer = scheduleTask(drain)
  }
  const drainAll = () => {
    if (frame !== undefined) cancelFrame(frame)
    if (flushTimer !== undefined) clearTimeout(flushTimer)
    if (dispatchTimer !== undefined) clearTimeout(dispatchTimer)
    scheduled = false
    frame = undefined
    flushTimer = undefined
    dispatchTimer = undefined
    reclassify()
    takeAll()
    while (hasDispatch()) options.emit(dispatch[dispatchIndex++]!)
    clearDispatch()
    resolveWaiters()
  }
  const dispose = () => {
    if (frame !== undefined) cancelFrame(frame)
    if (flushTimer !== undefined) clearTimeout(flushTimer)
    if (dispatchTimer !== undefined) clearTimeout(dispatchTimer)
    clearHeldTimer()
    queue.clear()
    held.clear()
    clearDispatch()
    resolveWaiters()
  }
  const settled = () => {
    if (!queue.length && !held.length && !hasDispatch()) return Promise.resolve()
    return new Promise<void>((resolve) => waiters.add(resolve))
  }
  const pressured = () => queue.pressured || held.pressured || dispatch.length - dispatchIndex >= 256
  /**
   * The set of background chats changed: within a frame, a chat that left it
   * gets its held events and a chat that joined it starts holding.
   */
  const backgroundChanged = () => schedule()
  return { enqueue, flush, drainAll, settled, pressured, visibilityChanged, backgroundChanged, dispose }
}
